// ============================================================================
// PRELIT-CLOSE-001 — Close Early Recovery Case (Win / Lost)
// ============================================================================
// Medium modal. The agent picks an outcome (Won/Lost), then a reason filtered
// by outcome_category, adds mandatory remarks, and (optionally for Won) a
// recovered amount. The backend auto-cancels any open commitment and routes
// the workflow through mark_won → CLOSED_RECOVERED or mark_lost → CLOSED_LOST.
//
// Spec: Resources/info 2.txt §1 — Ticket Closure.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useClosePreLitCase, usePreLitClosureReasons } from 'hooks/api/usePreLitigation';
import type { PreLitClosureReason } from 'api/types/preLitigation.types';

type OutcomeChoice = 'WON' | 'LOST';

interface FormState {
  outcome: OutcomeChoice;
  closureReasonId: string;
  closureRemarks: string;
  recoveredAmount: string;       // string-controlled input
}

interface CloseEarlyRecoveryModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  onClose: () => void;
  onClosed: () => void;
}

const buildInitial = (): FormState => ({
  outcome: 'WON',
  closureReasonId: '',
  closureRemarks: '',
  recoveredAmount: '',
});

const OUTCOME_PALETTE: Record<OutcomeChoice, { bg: string; border: string; text: string; pillBg: string; }> = {
  WON:  { bg: '#f0fdf4', border: '#16a34a', text: '#166534', pillBg: '#dcfce7' },
  LOST: { bg: '#fef2f2', border: '#dc2626', text: '#991b1b', pillBg: '#fee2e2' },
};

const CloseEarlyRecoveryModal: React.FC<CloseEarlyRecoveryModalProps> = ({
  isOpen, caseId, caseCode, onClose, onClosed,
}) => {
  const [form, setForm] = useState<FormState>(buildInitial());
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const closeCase = useClosePreLitCase();
  const { data: reasons, loading: reasonsLoading } = usePreLitClosureReasons(isOpen ? caseId : null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial();
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(
    () => !!snapRef.current && JSON.stringify(form) !== snapRef.current,
    [form],
  );

  const setField = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(prev => { const next = { ...prev }; delete next[field as string]; return next; });
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Filter reasons by the chosen outcome — WON shows FAVOURABLE; LOST shows
  // UNFAVOURABLE + NEUTRAL (both close to CLOSED_LOST on the backend).
  const reasonOptions = useMemo(() => {
    const list = (reasons ?? []).filter((r: PreLitClosureReason) => {
      if (form.outcome === 'WON')  return r.outcomeCategory === 'FAVOURABLE';
      return r.outcomeCategory === 'UNFAVOURABLE' || r.outcomeCategory === 'NEUTRAL';
    });
    return list.map(r => ({ value: r.id, label: r.closureName }));
  }, [reasons, form.outcome]);

  // When outcome changes, drop the previous reason if it no longer matches.
  useEffect(() => {
    if (!form.closureReasonId) return;
    const stillValid = reasonOptions.some(o => o.value === form.closureReasonId);
    if (!stillValid) setForm(prev => ({ ...prev, closureReasonId: '' }));
  }, [form.outcome, form.closureReasonId, reasonOptions]);

  const handleClose = useCallback(() => {
    if (closeCase.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, closeCase.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.closureReasonId) errors.closureReasonId = 'Required';
    const remarks = form.closureRemarks.trim();
    if (remarks.length < 10) errors.closureRemarks = 'Remarks must be at least 10 characters.';
    if (remarks.length > 2000) errors.closureRemarks = 'Max 2000 characters.';

    if (form.outcome === 'WON' && form.recoveredAmount) {
      const amt = Number(form.recoveredAmount);
      if (!isFinite(amt) || amt <= 0) errors.recoveredAmount = 'Amount must be a positive number.';
    }

    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      await closeCase.run(caseId, {
        closureReasonId: form.closureReasonId,
        closureRemarks: form.closureRemarks.trim(),
        recoveredAmount: form.outcome === 'WON' && form.recoveredAmount
          ? Number(form.recoveredAmount) : undefined,
      });
      notificationService.toast.success(
        `Case ${caseCode} closed as ${form.outcome === 'WON' ? 'Won' : 'Lost'}.`,
      );
      onClosed();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to close case.';
      notificationService.toast.error(msg, 6000);
    }
  }, [closeCase, caseId, caseCode, form, onClosed, onClose]);

  if (!isOpen) return null;

  const palette = OUTCOME_PALETTE[form.outcome];

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {closeCase.loading && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'var(--modal-loading-overlay, rgba(15, 23, 42, 0.35))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10,
          }}>
            <div style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 600 }}>Closing case…</div>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Close Case — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={closeCase.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Outcome toggle (Won / Lost) — large, paletted */}
          <div className="form-group" data-field="outcome">
            <label className="form-label">Outcome <span className="text-danger">*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {(['WON', 'LOST'] as OutcomeChoice[]).map(o => {
                const p = OUTCOME_PALETTE[o];
                const active = form.outcome === o;
                return (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setField('outcome', o)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      padding: '0.7rem 1rem',
                      fontSize: '0.95rem', fontWeight: 700,
                      color: active ? p.text : 'var(--text-secondary)',
                      background: active ? p.bg : 'transparent',
                      border: `2px solid ${active ? p.border : 'var(--border-color)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.12s',
                    }}
                  >
                    {o === 'WON' ? <IconSystem.checkCircle size={18} /> : <IconSystem.xCircle size={18} />}
                    {o === 'WON' ? 'Won (Recovery)' : 'Lost (No recovery)'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reason — filtered by outcome */}
          <div className="form-group" data-field="closureReasonId">
            <SyutSelect
              label={<>Closure Reason <span className="text-danger">*</span></>}
              options={reasonOptions}
              value={form.closureReasonId}
              onChange={(v) => setField('closureReasonId', (v as string) ?? '')}
              isClearable={false}
              isLoading={reasonsLoading}
              placeholder={reasonsLoading ? 'Loading…' : (reasonOptions.length === 0 ? 'No reasons configured' : 'Select a reason…')}
              error={fieldErrors.closureReasonId}
            />
          </div>

          {/* Optional recovered amount — only for Won */}
          {form.outcome === 'WON' && (
            <div className="form-group" data-field="recoveredAmount">
              <label className="form-label">Recovered Amount (₹) <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>(optional)</span></label>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                className={`form-input${fieldErrors.recoveredAmount ? ' form-field-error' : ''}`}
                value={form.recoveredAmount}
                onChange={(e) => setField('recoveredAmount', e.target.value)}
                placeholder="e.g. 50000 (if paid directly to the bank)"
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Used when the borrower paid outside the recovery process (e.g. directly to the branch).
              </div>
              {fieldErrors.recoveredAmount && (
                <div className="form-error-message">{fieldErrors.recoveredAmount}</div>
              )}
            </div>
          )}

          {/* Remarks — always required */}
          <div className="form-group" data-field="closureRemarks">
            <label className="form-label">
              Remarks <span className="text-danger">*</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: '0.4rem' }}>(min 10 chars)</span>
            </label>
            <textarea
              className={`form-textarea${fieldErrors.closureRemarks ? ' form-field-error' : ''}`}
              rows={4}
              maxLength={2000}
              value={form.closureRemarks}
              onChange={(e) => setField('closureRemarks', e.target.value)}
              placeholder={form.outcome === 'WON'
                ? 'e.g. Customer confirmed payment via UPI on 23-Jun; reflected in branch statement.'
                : 'e.g. Customer not reachable after 5 calls + 2 visits; address invalid.'}
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              {form.closureRemarks.trim().length}/2000 characters
            </div>
            {fieldErrors.closureRemarks && (
              <div className="form-error-message">{fieldErrors.closureRemarks}</div>
            )}
          </div>

          {/* Side-effect hint */}
          <div style={{
            padding: '0.6rem 0.8rem',
            background: palette.bg,
            border: `1px solid ${palette.border}`,
            borderRadius: 6,
            color: palette.text,
            fontSize: '0.78rem',
          }}>
            <strong>On save:</strong> the case will be moved to {form.outcome === 'WON' ? '"Closed — Recovered"' : '"Closed — Lost"'},
            any open commitment will be automatically cancelled, and the closure becomes part of the case audit history.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={closeCase.loading}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={closeCase.loading}
            style={form.outcome === 'LOST' ? { background: '#dc2626', borderColor: '#b91c1c' } : undefined}
          >
            {form.outcome === 'WON' ? 'Close as Won' : 'Close as Lost'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Close"
        message={`Close case ${caseCode} as ${form.outcome === 'WON' ? 'Won' : 'Lost'}? This will end the workflow.`}
        confirmLabel={form.outcome === 'WON' ? 'Close as Won' : 'Close as Lost'}
        cancelLabel="Cancel"
        variant={form.outcome === 'WON' ? 'success' : 'danger'}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved closure details. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CloseEarlyRecoveryModal;
